import React, { useEffect, useState } from 'react';
import portalApi from '../../../../lib/portalApi';
import {
  RefreshCw,
  ExternalLink,
  Lock,
  Mail,
  UserPlus,
  X,
  CheckCircle2,
  AlertCircle,
  HelpCircle,
  Shield,
  FileText,
  Video,
} from 'lucide-react';

export default function StartupSupportPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // [4] Notifications toggle state (persisted in localStorage)
  const NOTIF_KEY = 'innovher_notification_prefs_v1';
  const [notifications, setNotifications] = useState(() => {
    try {
      const saved = localStorage.getItem(NOTIF_KEY);
      if (saved) return JSON.parse(saved);
    } catch (e) {
      // ignore
    }
    return {
      taskAssigned: true,
      docRejected: true,
      meetingAdded: false,
    };
  });

  const toggleNotification = (key) => {
    setNotifications((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try {
        localStorage.setItem(NOTIF_KEY, JSON.stringify(next));
      } catch (e) {
        // ignore
      }
      return next;
    });
  };

  // Modals state
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);

  // Support inquiry form state
  const [inquiryForm, setInquiryForm] = useState({
    subject: '',
    category: 'Program Support',
    message: '',
  });
  const [inquirySubmitting, setInquirySubmitting] = useState(false);
  const [inquirySuccess, setInquirySuccess] = useState('');
  const [inquiryError, setInquiryError] = useState('');

  // Password change state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // Team invite state
  const [inviteForm, setInviteForm] = useState({
    name: '',
    email: '',
    role: 'Co-founder',
    scope: 'Full access',
  });
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState('');

  const loadPageData = async () => {
    try {
      setLoading(true);
      const res = await portalApi.getStartupPortalSupport();
      setData(res);
    } catch (err) {
      console.error('Failed to load resources and settings data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPageData();
  }, []);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!inquiryForm.subject.trim() || !inquiryForm.message.trim()) {
      setInquiryError('Please enter subject and message details.');
      return;
    }
    try {
      setInquirySubmitting(true);
      setInquiryError('');
      await portalApi.submitStartupPortalTicket(inquiryForm);
      setInquirySuccess('Inquiry sent to program team. An accelerator director will reply shortly.');
      setInquiryForm({ subject: '', category: 'Program Support', message: '' });
      setTimeout(() => {
        setInquirySuccess('');
        setContactModalOpen(false);
      }, 2000);
      await loadPageData();
    } catch (err) {
      setInquiryError(err.message || 'Failed to submit inquiry.');
    } finally {
      setInquirySubmitting(false);
    }
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('New password and confirm password do not match.');
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters long.');
      return;
    }
    setPasswordError('');
    setPasswordSuccess('Password updated successfully.');
    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setTimeout(() => {
      setPasswordSuccess('');
      setPasswordModalOpen(false);
    }, 2000);
  };

  const handleInviteSubmit = async (e) => {
    e.preventDefault();
    try {
      setInviteSubmitting(true);
      await portalApi.inviteStartupPortalTeamMember({
        name: inviteForm.name,
        email: inviteForm.email,
        role: inviteForm.role,
        permissions: ['vault', 'tasks', 'metrics'],
      });
      setInviteSuccess(`Invitation sent to ${inviteForm.email}.`);
      setInviteForm({ name: '', email: '', role: 'Co-founder', scope: 'Full access' });
      setTimeout(() => {
        setInviteSuccess('');
        setInviteModalOpen(false);
      }, 2000);
      await loadPageData();
    } catch (err) {
      alert(err.message || 'Failed to invite team member');
    } finally {
      setInviteSubmitting(false);
    }
  };

  // Announcements list matching wireframe
  const announcements = data?.announcements || [
    {
      id: 'ann_1',
      title: 'Demo Day dates out',
      author: 'Admin',
      date: '28 Oct',
      isNew: true,
      details: 'The cohort Demo Day is scheduled for the last week of November. Investor invitations have been sent.',
    },
    {
      id: 'ann_2',
      title: 'Workshop: unit economics',
      author: 'Admin',
      date: '24 Oct',
      isNew: false,
      details: 'Interactive session with growth mentor on CAC/LTV benchmarks, gross margins, and pricing strategy.',
    },
    {
      id: 'ann_3',
      title: 'Holiday schedule',
      author: 'Admin',
      date: '20 Oct',
      isNew: false,
      details: 'Accelerator office and advisory sessions holiday schedule for the upcoming festival week.',
    },
  ];

  // Resources list matching wireframe
  const resources = data?.curatedResources || [
    {
      id: 'res_1',
      title: 'Pitch deck template',
      type: 'PDF',
      size: '2 MB',
      actionLabel: 'Open',
      url: 'https://docs.google.com/presentation/d/pitch-deck-template',
    },
    {
      id: 'res_2',
      title: 'Legal checklist',
      type: 'PDF',
      size: '1 MB',
      actionLabel: 'Open',
      url: 'https://docs.google.com/document/d/legal-checklist',
    },
    {
      id: 'res_3',
      title: 'Workshop recording',
      type: 'Video',
      duration: '48 min',
      actionLabel: 'Watch',
      url: 'https://youtube.com/watch?v=innovher-workshop-session',
    },
  ];

  // Team list matching wireframe
  const team = data?.team || [
    {
      id: 'tm_1',
      name: data?.account?.name || 'Ankit Rao',
      role: 'Founder',
      scope: 'Full access · cannot be removed',
      badge: 'You',
      badgeType: 'you',
    },
    {
      id: 'tm_2',
      name: 'Meera Shah',
      role: 'Co-founder',
      scope: 'Full access',
      badge: 'Active',
      badgeType: 'active',
    },
    {
      id: 'tm_3',
      name: 'Dev Patel',
      role: 'Team member',
      scope: 'Documents and Tasks only',
      badge: 'Invited',
      badgeType: 'invited',
    },
  ];

  const accountName = data?.account?.name || 'Ankit Rao';

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-[#F2F2F4] tracking-tight">
            Resources & Settings
          </h1>
          <p className="text-xs text-[#8E8E99] mt-0.5">
            Cohort announcements, toolkits, notification preferences, account credentials, and team access.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={loadPageData}
            className="p-2 rounded-xl border border-[#2C2C34] bg-[#131318] text-[#8E8E99] hover:text-white transition-colors"
            title="Refresh data"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* ========================================================
          ROW 1: [1] Announcements (Left) + [2] Resources (Right)
         ======================================================== */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* [1] ANNOUNCEMENTS */}
        <div className="bg-[#131318] border border-[#2C2C34] rounded-2xl p-5 sm:p-6 space-y-4">
          <div className="flex items-center justify-between pb-2 border-b border-[#2C2C34]/60">
            <h2 className="text-sm font-bold text-[#F2F2F4]">Announcements</h2>
            <span className="text-[11px] text-[#8E8E99]">Admin updates</span>
          </div>

          <div className="space-y-3">
            {announcements.map((ann) => (
              <div
                key={ann.id}
                onClick={() => setSelectedAnnouncement(ann)}
                className="p-3.5 rounded-xl bg-[#08080A] border border-[#2C2C34] hover:border-[#3E3E48] transition-colors flex items-center justify-between gap-3 cursor-pointer group"
              >
                <div>
                  <div className="text-xs font-bold text-[#F2F2F4] group-hover:text-[#FF8EC5] transition-colors">
                    {ann.title}
                  </div>
                  <div className="text-[11px] text-[#8E8E99] mt-0.5">
                    {ann.author} · {ann.date}
                  </div>
                </div>

                {ann.isNew && (
                  <span className="px-3 py-0.5 rounded-full border border-[#D42B92] text-[#FF8EC5] text-[11px] font-semibold">
                    New
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* [2] RESOURCES */}
        <div className="bg-[#131318] border border-[#2C2C34] rounded-2xl p-5 sm:p-6 space-y-4">
          <div className="flex items-center justify-between pb-2 border-b border-[#2C2C34]/60">
            <h2 className="text-sm font-bold text-[#F2F2F4]">Resources</h2>
            <span className="text-[11px] text-[#8E8E99]">Program templates</span>
          </div>

          <div className="space-y-3">
            {resources.map((res) => {
              const subtext = res.size
                ? `${res.type} · ${res.size}`
                : `${res.type} · ${res.duration || '48 min'}`;

              return (
                <div
                  key={res.id}
                  className="p-3.5 rounded-xl bg-[#08080A] border border-[#2C2C34] hover:border-[#3E3E48] transition-colors flex items-center justify-between gap-3"
                >
                  <div>
                    <div className="text-xs font-bold text-[#F2F2F4]">{res.title}</div>
                    <div className="text-[11px] text-[#8E8E99] mt-0.5">{subtext}</div>
                  </div>

                  <a
                    href={res.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-1 rounded-full border border-[#38BDF8] text-[#38BDF8] hover:bg-[#38BDF8] hover:text-black text-xs font-semibold transition-all shrink-0 inline-flex items-center gap-1"
                  >
                    <span>{res.actionLabel || (res.type === 'Video' ? 'Watch' : 'Open')}</span>
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ========================================================
          ROW 2: [3] Support + [4] Notifications + [5] Account
         ======================================================== */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
        {/* [3] SUPPORT */}
        <div className="bg-[#131318] border border-[#2C2C34] rounded-2xl p-5 sm:p-6 space-y-4 flex flex-col justify-between">
          <div>
            <h2 className="text-sm font-bold text-[#F2F2F4] pb-2 border-b border-[#2C2C34]/60">
              Support
            </h2>
            <div className="pt-3 space-y-1">
              <div className="text-xs font-bold text-[#F2F2F4]">
                Ask the program team
              </div>
              <div className="text-xs text-[#A855F7] font-semibold">
                Reply time: To Confirm
              </div>
            </div>
          </div>

          <div className="pt-4">
            <button
              onClick={() => setContactModalOpen(true)}
              className="px-6 py-2 rounded-full bg-gradient-to-r from-[#D42B92] to-[#B02279] hover:brightness-110 text-white font-semibold text-xs shadow-md shadow-[#D42B92]/30 transition-all"
            >
              Contact us
            </button>
          </div>
        </div>

        {/* [4] NOTIFICATIONS */}
        <div className="bg-[#131318] border border-[#2C2C34] rounded-2xl p-5 sm:p-6 space-y-4 flex flex-col justify-between">
          <div>
            <h2 className="text-sm font-bold text-[#F2F2F4] pb-2 border-b border-[#2C2C34]/60">
              Notifications
            </h2>

            <div className="space-y-3 pt-3">
              {/* Task assigned */}
              <div className="flex items-center justify-between text-xs">
                <span className="font-semibold text-[#F2F2F4]">Task assigned</span>
                <button
                  type="button"
                  onClick={() => toggleNotification('taskAssigned')}
                  className={`px-3 py-0.5 rounded-full text-xs font-semibold transition-all ${
                    notifications.taskAssigned
                      ? 'border border-emerald-500/50 bg-emerald-500/10 text-emerald-400'
                      : 'border border-[#2C2C34] bg-[#18181F] text-[#8E8E99]'
                  }`}
                >
                  {notifications.taskAssigned ? 'On' : 'Off'}
                </button>
              </div>

              {/* Doc rejected */}
              <div className="flex items-center justify-between text-xs">
                <span className="font-semibold text-[#F2F2F4]">Doc rejected</span>
                <button
                  type="button"
                  onClick={() => toggleNotification('docRejected')}
                  className={`px-3 py-0.5 rounded-full text-xs font-semibold transition-all ${
                    notifications.docRejected
                      ? 'border border-emerald-500/50 bg-emerald-500/10 text-emerald-400'
                      : 'border border-[#2C2C34] bg-[#18181F] text-[#8E8E99]'
                  }`}
                >
                  {notifications.docRejected ? 'On' : 'Off'}
                </button>
              </div>

              {/* Meeting added */}
              <div className="flex items-center justify-between text-xs">
                <span className="font-semibold text-[#F2F2F4]">Meeting added</span>
                <button
                  type="button"
                  onClick={() => toggleNotification('meetingAdded')}
                  className={`px-3 py-0.5 rounded-full text-xs font-semibold transition-all ${
                    notifications.meetingAdded
                      ? 'border border-emerald-500/50 bg-emerald-500/10 text-emerald-400'
                      : 'border border-[#2C2C34] bg-[#18181F] text-[#8E8E99]'
                  }`}
                >
                  {notifications.meetingAdded ? 'On' : 'Off'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* [5] ACCOUNT */}
        <div className="bg-[#131318] border border-[#2C2C34] rounded-2xl p-5 sm:p-6 space-y-4 flex flex-col justify-between">
          <div>
            <h2 className="text-sm font-bold text-[#F2F2F4] pb-2 border-b border-[#2C2C34]/60">
              Account
            </h2>

            <div className="space-y-3 pt-3">
              {/* Name */}
              <div className="flex items-center justify-between text-xs">
                <span className="text-[#8E8E99]">Name</span>
                <span className="font-bold text-[#F2F2F4]">{accountName}</span>
              </div>

              {/* Password */}
              <div className="flex items-center justify-between text-xs">
                <span className="text-[#8E8E99]">Password</span>
                <button
                  onClick={() => setPasswordModalOpen(true)}
                  className="font-bold text-[#F2F2F4] hover:text-[#D42B92] hover:underline transition-colors"
                >
                  Change
                </button>
              </div>

              {/* 2-step */}
              <div className="flex items-center justify-between text-xs">
                <span className="text-[#8E8E99]">2-step</span>
                <span className="px-3 py-0.5 rounded-full border border-[#A855F7]/40 bg-[#A855F7]/10 text-[#C084FC] font-semibold text-xs">
                  To Confirm
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ========================================================
          ROW 3: [6] Team access (Full width bottom card)
         ======================================================== */}
      <div className="bg-[#131318] border border-[#2C2C34] rounded-2xl p-5 sm:p-6 space-y-4">
        <div className="flex items-center justify-between pb-2 border-b border-[#2C2C34]/60">
          <h2 className="text-sm font-bold text-[#F2F2F4]">Team access</h2>
          <button
            onClick={() => setInviteModalOpen(true)}
            className="text-xs text-[#FF8EC5] hover:text-white font-medium flex items-center gap-1 transition-colors"
          >
            <UserPlus className="w-3.5 h-3.5" />
            <span>Invite member</span>
          </button>
        </div>

        <div className="space-y-2.5">
          {team.map((member) => {
            const isYou = member.badgeType === 'you' || member.badge === 'You';
            const isActive = member.badgeType === 'active' || member.badge === 'Active';
            const isInvited = member.badgeType === 'invited' || member.badge === 'Invited';

            return (
              <div
                key={member.id}
                className="p-3.5 rounded-xl bg-[#08080A] border border-[#2C2C34] flex items-center justify-between gap-3"
              >
                <div className="min-w-0">
                  <div className="text-xs text-[#F2F2F4] truncate">
                    <span className="font-bold">{member.name}</span>
                    <span className="text-[#8E8E99] font-medium"> · {member.role}</span>
                    <span className="text-[#6E6E78] text-[11px] ml-2 hidden sm:inline">
                      {member.scope}
                    </span>
                  </div>
                  <div className="text-[11px] text-[#6E6E78] sm:hidden mt-0.5">
                    {member.scope}
                  </div>
                </div>

                {/* Right Badge */}
                <div className="shrink-0">
                  {isYou && (
                    <span className="px-3.5 py-1 rounded-full bg-[#1C1C24] border border-[#2C2C34] text-[#8E8E99] text-xs font-semibold">
                      You
                    </span>
                  )}
                  {isActive && (
                    <span className="px-3.5 py-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-400 text-xs font-semibold">
                      Active
                    </span>
                  )}
                  {isInvited && (
                    <span className="px-3.5 py-1 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-400 text-xs font-semibold">
                      Invited
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ========================================================
          MODAL 1: Contact Us (Support Ticket Form)
         ======================================================== */}
      {contactModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-150">
          <div className="w-full max-w-md rounded-2xl border border-[#2C2C34] bg-[#131318] p-6 shadow-2xl space-y-4 text-[#F2F2F4]">
            <div className="flex items-center justify-between border-b border-[#2C2C34] pb-3">
              <div>
                <h3 className="text-base font-bold">Ask the Program Team</h3>
                <p className="text-xs text-[#8E8E99] mt-0.5">
                  Direct inquiry to accelerator operations & lead mentors.
                </p>
              </div>
              <button
                onClick={() => setContactModalOpen(false)}
                className="p-1 rounded-lg text-[#8E8E99] hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {inquirySuccess && (
              <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 shrink-0" />
                <span>{inquirySuccess}</span>
              </div>
            )}

            {inquiryError && (
              <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-400 text-xs">
                {inquiryError}
              </div>
            )}

            <form onSubmit={handleContactSubmit} className="space-y-3.5 text-xs">
              <div>
                <label className="text-[#8E8E99] font-medium block mb-1">
                  Subject *
                </label>
                <input
                  type="text"
                  value={inquiryForm.subject}
                  onChange={(e) =>
                    setInquiryForm({ ...inquiryForm, subject: e.target.value })
                  }
                  className="w-full px-3.5 py-2.5 rounded-xl border border-[#2C2C34] bg-[#08080A] text-[#F2F2F4] focus:outline-none focus:border-[#D42B92]"
                  placeholder="e.g. Question on Series Seed financial template"
                  required
                />
              </div>

              <div>
                <label className="text-[#8E8E99] font-medium block mb-1">
                  Category
                </label>
                <select
                  value={inquiryForm.category}
                  onChange={(e) =>
                    setInquiryForm({ ...inquiryForm, category: e.target.value })
                  }
                  className="w-full px-3.5 py-2.5 rounded-xl border border-[#2C2C34] bg-[#08080A] text-[#F2F2F4] focus:outline-none focus:border-[#D42B92]"
                >
                  <option value="Program Deliverables">Program Deliverables</option>
                  <option value="Mentor Matching">Mentor Matching</option>
                  <option value="Investor Readiness">Investor Readiness</option>
                  <option value="Legal & Compliance">Legal & Compliance</option>
                  <option value="General Inquiry">General Inquiry</option>
                </select>
              </div>

              <div>
                <label className="text-[#8E8E99] font-medium block mb-1">
                  Message *
                </label>
                <textarea
                  rows={4}
                  value={inquiryForm.message}
                  onChange={(e) =>
                    setInquiryForm({ ...inquiryForm, message: e.target.value })
                  }
                  className="w-full px-3.5 py-2.5 rounded-xl border border-[#2C2C34] bg-[#08080A] text-[#F2F2F4] focus:outline-none focus:border-[#D42B92]"
                  placeholder="Describe your inquiry or request for the team..."
                  required
                />
              </div>

              <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-[#2C2C34]">
                <button
                  type="button"
                  onClick={() => setContactModalOpen(false)}
                  className="px-4 py-2 rounded-xl border border-[#2C2C34] text-xs text-[#8E8E99] hover:text-white"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={inquirySubmitting}
                  className="px-5 py-2 rounded-xl bg-gradient-to-r from-[#D42B92] to-[#B02279] text-white font-semibold text-xs transition-all shadow-md shadow-[#D42B92]/30 disabled:opacity-50"
                >
                  {inquirySubmitting ? 'Sending...' : 'Send Inquiry'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================
          MODAL 2: Change Password
         ======================================================== */}
      {passwordModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-150">
          <div className="w-full max-w-sm rounded-2xl border border-[#2C2C34] bg-[#131318] p-6 shadow-2xl space-y-4 text-[#F2F2F4]">
            <div className="flex items-center justify-between border-b border-[#2C2C34] pb-3">
              <h3 className="text-base font-bold">Change Password</h3>
              <button
                onClick={() => setPasswordModalOpen(false)}
                className="p-1 rounded-lg text-[#8E8E99] hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {passwordSuccess && (
              <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs">
                {passwordSuccess}
              </div>
            )}

            {passwordError && (
              <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-400 text-xs">
                {passwordError}
              </div>
            )}

            <form onSubmit={handlePasswordSubmit} className="space-y-3.5 text-xs">
              <div>
                <label className="text-[#8E8E99] font-medium block mb-1">
                  Current Password *
                </label>
                <input
                  type="password"
                  value={passwordForm.currentPassword}
                  onChange={(e) =>
                    setPasswordForm({ ...passwordForm, currentPassword: e.target.value })
                  }
                  className="w-full px-3.5 py-2 rounded-xl border border-[#2C2C34] bg-[#08080A] text-[#F2F2F4] focus:outline-none focus:border-[#D42B92]"
                  required
                />
              </div>

              <div>
                <label className="text-[#8E8E99] font-medium block mb-1">
                  New Password *
                </label>
                <input
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(e) =>
                    setPasswordForm({ ...passwordForm, newPassword: e.target.value })
                  }
                  className="w-full px-3.5 py-2 rounded-xl border border-[#2C2C34] bg-[#08080A] text-[#F2F2F4] focus:outline-none focus:border-[#D42B92]"
                  required
                />
              </div>

              <div>
                <label className="text-[#8E8E99] font-medium block mb-1">
                  Confirm New Password *
                </label>
                <input
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) =>
                    setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })
                  }
                  className="w-full px-3.5 py-2 rounded-xl border border-[#2C2C34] bg-[#08080A] text-[#F2F2F4] focus:outline-none focus:border-[#D42B92]"
                  required
                />
              </div>

              <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-[#2C2C34]">
                <button
                  type="button"
                  onClick={() => setPasswordModalOpen(false)}
                  className="px-4 py-2 rounded-xl border border-[#2C2C34] text-xs text-[#8E8E99] hover:text-white"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 rounded-xl bg-gradient-to-r from-[#D42B92] to-[#B02279] text-white font-semibold text-xs shadow-md shadow-[#D42B92]/30"
                >
                  Update Password
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================
          MODAL 3: Announcement Detail
         ======================================================== */}
      {selectedAnnouncement && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-150">
          <div className="w-full max-w-md rounded-2xl border border-[#2C2C34] bg-[#131318] p-6 shadow-2xl space-y-4 text-[#F2F2F4]">
            <div className="flex items-start justify-between border-b border-[#2C2C34] pb-3">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-bold">
                    {selectedAnnouncement.title}
                  </h3>
                  {selectedAnnouncement.isNew && (
                    <span className="px-2 py-0.2 rounded-full border border-[#D42B92] text-[#FF8EC5] text-[10px] font-semibold">
                      New
                    </span>
                  )}
                </div>
                <p className="text-xs text-[#8E8E99] mt-0.5">
                  Published by {selectedAnnouncement.author} · {selectedAnnouncement.date}
                </p>
              </div>
              <button
                onClick={() => setSelectedAnnouncement(null)}
                className="p-1 rounded-lg text-[#8E8E99] hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 rounded-xl bg-[#08080A] border border-[#2C2C34] text-xs text-[#8E8E99] leading-relaxed">
              {selectedAnnouncement.details}
            </div>

            <div className="flex items-center justify-end pt-2">
              <button
                onClick={() => setSelectedAnnouncement(null)}
                className="px-4 py-2 rounded-xl border border-[#2C2C34] text-xs text-[#8E8E99] hover:text-white"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================
          MODAL 4: Invite Team Member
         ======================================================== */}
      {inviteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-150">
          <div className="w-full max-w-md rounded-2xl border border-[#2C2C34] bg-[#131318] p-6 shadow-2xl space-y-4 text-[#F2F2F4]">
            <div className="flex items-center justify-between border-b border-[#2C2C34] pb-3">
              <div>
                <h3 className="text-base font-bold">Invite Team Member</h3>
                <p className="text-xs text-[#8E8E99] mt-0.5">
                  Grant collaborator access to startup dashboard.
                </p>
              </div>
              <button
                onClick={() => setInviteModalOpen(false)}
                className="p-1 rounded-lg text-[#8E8E99] hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {inviteSuccess && (
              <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs">
                {inviteSuccess}
              </div>
            )}

            <form onSubmit={handleInviteSubmit} className="space-y-3.5 text-xs">
              <div>
                <label className="text-[#8E8E99] font-medium block mb-1">
                  Full Name *
                </label>
                <input
                  type="text"
                  value={inviteForm.name}
                  onChange={(e) =>
                    setInviteForm({ ...inviteForm, name: e.target.value })
                  }
                  className="w-full px-3.5 py-2.5 rounded-xl border border-[#2C2C34] bg-[#08080A] text-[#F2F2F4] focus:outline-none focus:border-[#D42B92]"
                  placeholder="e.g. Dev Patel"
                  required
                />
              </div>

              <div>
                <label className="text-[#8E8E99] font-medium block mb-1">
                  Email Address *
                </label>
                <input
                  type="email"
                  value={inviteForm.email}
                  onChange={(e) =>
                    setInviteForm({ ...inviteForm, email: e.target.value })
                  }
                  className="w-full px-3.5 py-2.5 rounded-xl border border-[#2C2C34] bg-[#08080A] text-[#F2F2F4] focus:outline-none focus:border-[#D42B92]"
                  placeholder="dev@startup.com"
                  required
                />
              </div>

              <div>
                <label className="text-[#8E8E99] font-medium block mb-1">
                  Role
                </label>
                <select
                  value={inviteForm.role}
                  onChange={(e) =>
                    setInviteForm({ ...inviteForm, role: e.target.value })
                  }
                  className="w-full px-3.5 py-2.5 rounded-xl border border-[#2C2C34] bg-[#08080A] text-[#F2F2F4] focus:outline-none focus:border-[#D42B92]"
                >
                  <option value="Co-founder">Co-founder (Full access)</option>
                  <option value="Team member">Team member (Documents & Tasks only)</option>
                  <option value="Advisor">Advisor (Read-only)</option>
                </select>
              </div>

              <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-[#2C2C34]">
                <button
                  type="button"
                  onClick={() => setInviteModalOpen(false)}
                  className="px-4 py-2 rounded-xl border border-[#2C2C34] text-xs text-[#8E8E99] hover:text-white"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={inviteSubmitting}
                  className="px-5 py-2 rounded-xl bg-gradient-to-r from-[#D42B92] to-[#B02279] text-white font-semibold text-xs transition-all shadow-md shadow-[#D42B92]/30 disabled:opacity-50"
                >
                  {inviteSubmitting ? 'Sending...' : 'Send Invitation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
